import type { BossVariant, EnemyArchetype, EnemyKind, UpcomingEnemyPreview } from '../game/types';

export interface BossVariantProfile {
  labelKey: string;
  previewKey: string;
  hpBonus: number;
  speedBonus: number;
  frontArmorMultiplier: number;
  fireRateMultiplier: number;
  minFireRate: number;
  bulletSpeedMultiplier: number;
  bulletDamage: number;
  screenedPlayerShotChance: number;
  armorPiercingCadence: number;
  armorPiercingDamageMultiplier: number;
  armorPiercingBrickPierce: number;
  armorPiercingMetalPierce: number;
  shotWindup: number;
  closeShotWindup: number;
  shellSizeBonus: number;
  turnReactionSeconds: number;
  decisionMinSeconds: number;
  blockCommitSeconds: number;
  reverseStuckSeconds: number;
  pointBlankExtraRange: number;
  preferredRangeCells: number;
  evadeCooldownSeconds: number;
  evadeProjectileRange: number;
  snapReverseShotCooldownSeconds: number;
}

export const ENEMY_ARCHETYPES: Record<EnemyKind, EnemyArchetype> = {
  light: {
    kind: 'light',
    label: '侦察坦克',
    color: 0xc5563e,
    accent: 0xffb16d,
    maxSpeed: 122,
    hp: 1,
    fireRate: 1.65,
    bulletSpeed: 320,
    score: 120,
    aggression: 0.44,
    aimChance: 0.28,
    size: 30,
  },
  assault: {
    kind: 'assault',
    label: '突击坦克',
    color: 0xd16f31,
    accent: 0xffd05d,
    maxSpeed: 108,
    hp: 1,
    fireRate: 1.42,
    bulletSpeed: 360,
    score: 150,
    aggression: 0.58,
    aimChance: 0.38,
    size: 32,
  },
  heavy: {
    kind: 'heavy',
    label: '重装坦克',
    color: 0x8f4939,
    accent: 0xf09962,
    maxSpeed: 66,
    hp: 3,
    fireRate: 1.18,
    bulletSpeed: 350,
    score: 240,
    aggression: 0.88,
    aimChance: 0.52,
    size: 35,
  },
  sniper: {
    kind: 'sniper',
    label: '狙击坦克',
    color: 0x9b3e4f,
    accent: 0xff96aa,
    maxSpeed: 90,
    hp: 1,
    fireRate: 2.3,
    bulletSpeed: 455,
    score: 260,
    aggression: 0.35,
    aimChance: 0.82,
    size: 31,
  },
  boss: {
    kind: 'boss',
    label: '攻城主炮',
    color: 0x6d4633,
    accent: 0xffcc55,
    maxSpeed: 58,
    hp: 18,
    fireRate: 0.86,
    bulletSpeed: 430,
    score: 1200,
    aggression: 0.94,
    aimChance: 0.9,
    size: 44,
  },
};

export const BOSS_VARIANT_PROFILES: Record<BossVariant, BossVariantProfile> = {
  siege: {
    labelKey: 'bossVariant.siege',
    previewKey: 'bossVariant.preview.siege',
    hpBonus: 0,
    speedBonus: 0,
    frontArmorMultiplier: 0.06,
    fireRateMultiplier: 1,
    minFireRate: 0.68,
    bulletSpeedMultiplier: 1,
    bulletDamage: 3,
    screenedPlayerShotChance: 0,
    armorPiercingCadence: 0,
    armorPiercingDamageMultiplier: 1,
    armorPiercingBrickPierce: 0,
    armorPiercingMetalPierce: 0,
    shotWindup: 0.24,
    closeShotWindup: 0.14,
    shellSizeBonus: 0,
    turnReactionSeconds: 1.06,
    decisionMinSeconds: 1.34,
    blockCommitSeconds: 1.34,
    reverseStuckSeconds: 2.15,
    pointBlankExtraRange: 10,
    preferredRangeCells: 0,
    evadeCooldownSeconds: 0,
    evadeProjectileRange: 0,
    snapReverseShotCooldownSeconds: 0,
  },
  hunter: {
    labelKey: 'bossVariant.hunter',
    previewKey: 'bossVariant.preview.hunter',
    hpBonus: -4,
    speedBonus: 14,
    frontArmorMultiplier: 0.48,
    fireRateMultiplier: 0.52,
    minFireRate: 0.48,
    bulletSpeedMultiplier: 1.24,
    bulletDamage: 2.2,
    screenedPlayerShotChance: 2.4,
    armorPiercingCadence: 5,
    armorPiercingDamageMultiplier: 1.45,
    armorPiercingBrickPierce: 2,
    armorPiercingMetalPierce: 0,
    shotWindup: 0.13,
    closeShotWindup: 0.08,
    shellSizeBonus: -0.45,
    turnReactionSeconds: 0.98,
    decisionMinSeconds: 1.22,
    blockCommitSeconds: 1.18,
    reverseStuckSeconds: 2.2,
    pointBlankExtraRange: 9,
    preferredRangeCells: 0,
    evadeCooldownSeconds: 0,
    evadeProjectileRange: 0,
    snapReverseShotCooldownSeconds: 0,
  },
  bulwark: {
    labelKey: 'bossVariant.bulwark',
    previewKey: 'bossVariant.preview.bulwark',
    hpBonus: 6,
    speedBonus: -12,
    frontArmorMultiplier: 0.025,
    fireRateMultiplier: 1.42,
    minFireRate: 0.68,
    bulletSpeedMultiplier: 0.68,
    bulletDamage: 4.6,
    screenedPlayerShotChance: 0,
    armorPiercingCadence: 0,
    armorPiercingDamageMultiplier: 1,
    armorPiercingBrickPierce: 0,
    armorPiercingMetalPierce: 0,
    shotWindup: 0.42,
    closeShotWindup: 0.26,
    shellSizeBonus: 1.1,
    turnReactionSeconds: 1.42,
    decisionMinSeconds: 1.72,
    blockCommitSeconds: 1.78,
    reverseStuckSeconds: 2.9,
    pointBlankExtraRange: 13,
    preferredRangeCells: 0,
    evadeCooldownSeconds: 0,
    evadeProjectileRange: 0,
    snapReverseShotCooldownSeconds: 0,
  },
  sniper: {
    labelKey: 'bossVariant.sniper',
    previewKey: 'bossVariant.preview.sniper',
    hpBonus: -5,
    speedBonus: 56,
    frontArmorMultiplier: 1,
    fireRateMultiplier: 0.62,
    minFireRate: 0.52,
    bulletSpeedMultiplier: 1.24,
    bulletDamage: 4,
    screenedPlayerShotChance: 1.2,
    armorPiercingCadence: 1,
    armorPiercingDamageMultiplier: 1.25,
    armorPiercingBrickPierce: 2,
    armorPiercingMetalPierce: 0,
    shotWindup: 0.18,
    closeShotWindup: 0.1,
    shellSizeBonus: -0.75,
    turnReactionSeconds: 0.82,
    decisionMinSeconds: 0.78,
    blockCommitSeconds: 0.78,
    reverseStuckSeconds: 1.85,
    pointBlankExtraRange: 8,
    preferredRangeCells: 5.2,
    evadeCooldownSeconds: 1.65,
    evadeProjectileRange: 118,
    snapReverseShotCooldownSeconds: 3.4,
  },
};

export function pickEnemyKind(level: number, spawned: number, total: number, random = Math.random): EnemyKind {
  if (level % 5 === 0 && spawned === total - 1) return 'boss';
  const roll = random();
  const heavyChance = Math.min(0.24, level * 0.018);
  const sniperChance = Math.min(0.18, Math.max(0, level - 2) * 0.018);
  const assaultChance = Math.min(0.38, 0.18 + level * 0.014);
  if (roll < sniperChance) return 'sniper';
  if (roll < sniperChance + heavyChance) return 'heavy';
  if (roll < sniperChance + heavyChance + assaultChance) return 'assault';
  return 'light';
}

export function getBossVariantForLevel(level: number): BossVariant {
  const variants: BossVariant[] = ['siege', 'hunter', 'bulwark', 'sniper'];
  const bossIndex = Math.max(0, Math.floor(level / 5) - 1);
  return variants[bossIndex % variants.length];
}

export function buildEnemySpawnQueue(level: number, total: number, random = Math.random): UpcomingEnemyPreview[] {
  return Array.from({ length: total }, (_, spawned) => {
    const kind = pickEnemyKind(level, spawned, total, random);
    return kind === 'boss'
      ? { kind, bossVariant: getBossVariantForLevel(level) }
      : { kind };
  });
}
